<script setup lang="ts">
import maplibregl, { type GeoJSONSource, type Map as MapLibreMap } from 'maplibre-gl'
import { computed, inject, onUnmounted, ref, shallowRef, watch, type Ref } from 'vue'

import { logService, LOG_CATEGORIES } from '@/services/logService'
import type {
  TrackProfileAnalysis,
  TrackProfilePoint,
  TrackProfileVisibleRange,
} from '@/utils/trackProfileAnalysis'
import { findTrackProfilePositionAtDistance } from '@/utils/trackProfileAnalysis'

defineOptions({ name: 'TrackSegmentSelectionLayer' })

const props = defineProps<{
  analysis: TrackProfileAnalysis
  range: TrackProfileVisibleRange | null
  /** The selected track's own colour. The selection is drawn in this colour at normal weight. */
  accentColor?: string
}>()

const FALLBACK_ACCENT = '#3388ff'
// The selection renders in the track's own colour at its normal weight — focus comes from dimming
// the rest of the track (WorkspaceTourLayer `dimmed`), not from recolouring/thickening the selected
// part. Used for both the selected line and the distance-tick dots.
const selectionColor = computed(() => props.accentColor ?? FALLBACK_ACCENT)

const emit = defineEmits<{
  'update:range': [range: TrackProfileVisibleRange | null]
}>()

const mapInstance = inject<Ref<MapLibreMap | null>>('mapInstance', ref(null))
const mapLoaded = inject<Ref<boolean>>('mapLoaded', ref(false))

const LINE_SOURCE_ID = 'track-segment-selection-line-source'
const LINE_HALO_LAYER_ID = 'track-segment-selection-line-halo-layer'
const LINE_LAYER_ID = 'track-segment-selection-line-layer'
const HANDLE_SOURCE_ID = 'track-segment-selection-handle-source'
const HANDLE_HALO_LAYER_ID = 'track-segment-selection-handle-halo-layer'
const HANDLE_LAYER_ID = 'track-segment-selection-handle-layer'
const HANDLE_LABEL_LAYER_ID = 'track-segment-selection-handle-label-layer'
const TICK_SOURCE_ID = 'track-segment-selection-tick-source'
const TICK_CIRCLE_LAYER_ID = 'track-segment-selection-tick-circle-layer'
const TICK_LABEL_LAYER_ID = 'track-segment-selection-tick-label-layer'
const MIN_HANDLE_GAP_M = 10
const EARTH_RADIUS_M = 6371e3
const DEG_TO_RAD = Math.PI / 180

let layersInitialized = false
let boundMap: MapLibreMap | null = null
let activeHandle: 'start' | 'end' | null = null
const mapZoom = shallowRef(0)

type HandleFeatureProperties = {
  handle: 'start' | 'end'
  label: 'A' | 'B'
}

type TickFeatureProperties = {
  label: string
  radius: number
}

const totalDistanceM = computed(() => Math.max(props.analysis.totalDistanceM, 0))
const effectiveRange = computed<TrackProfileVisibleRange>(() => {
  const total = totalDistanceM.value
  const start = Math.max(0, Math.min(props.range?.start ?? 0, total))
  const end = Math.max(start, Math.min(props.range?.end ?? total, total))
  return { start, end }
})

const formatTickDistance = (distanceM: number) =>
  distanceM >= 1000 ? `${Math.round(distanceM / 1000)}` : `${Math.round(distanceM)}m`

const normalizeRange = (
  range: TrackProfileVisibleRange,
  draggedHandle?: 'start' | 'end',
): TrackProfileVisibleRange => {
  const total = totalDistanceM.value
  const start = Math.max(0, Math.min(range.start, total))
  const end = Math.max(0, Math.min(range.end, total))

  if (draggedHandle === 'start') {
    return {
      start: Math.min(start, Math.max(0, end - MIN_HANDLE_GAP_M)),
      end,
    }
  }

  if (draggedHandle === 'end') {
    return {
      start,
      end: Math.max(end, Math.min(total, start + MIN_HANDLE_GAP_M)),
    }
  }

  return {
    start: Math.min(start, end),
    end: Math.max(start, end),
  }
}

const getPointAtDistance = (distanceM: number): TrackProfilePoint | null =>
  findTrackProfilePositionAtDistance(props.analysis.points, distanceM)

const buildLineCoordinates = (): [number, number][] => {
  const range = effectiveRange.value
  const startPoint = getPointAtDistance(range.start)
  const endPoint = getPointAtDistance(range.end)
  if (!startPoint || !endPoint) return []

  const middleCoordinates = props.analysis.points
    .filter((point) => point.distanceM > range.start && point.distanceM < range.end)
    .map((point) => [point.lon, point.lat] as [number, number])

  return [[startPoint.lon, startPoint.lat], ...middleCoordinates, [endPoint.lon, endPoint.lat]]
}

const buildLineFC = (): GeoJSON.FeatureCollection<GeoJSON.LineString> => {
  const coordinates = buildLineCoordinates()
  if (coordinates.length < 2) return { type: 'FeatureCollection', features: [] }

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates,
        },
      },
    ],
  }
}

const buildHandleFC = (): GeoJSON.FeatureCollection<GeoJSON.Point, HandleFeatureProperties> => {
  const startPoint = getPointAtDistance(effectiveRange.value.start)
  const endPoint = getPointAtDistance(effectiveRange.value.end)
  const features: Array<GeoJSON.Feature<GeoJSON.Point, HandleFeatureProperties>> = []

  if (startPoint) {
    features.push({
      type: 'Feature',
      properties: { handle: 'start', label: 'A' },
      geometry: { type: 'Point', coordinates: [startPoint.lon, startPoint.lat] },
    })
  }

  if (endPoint) {
    features.push({
      type: 'Feature',
      properties: { handle: 'end', label: 'B' },
      geometry: { type: 'Point', coordinates: [endPoint.lon, endPoint.lat] },
    })
  }

  return {
    type: 'FeatureCollection',
    features,
  }
}

const getTickStepM = (selectedDistanceM: number) => {
  const zoom = mapZoom.value
  const preferredStepM = zoom >= 15 ? 1000 : zoom >= 10.5 ? 2000 : zoom >= 8.5 ? 5000 : 10000
  const candidateStepsM = [preferredStepM, 2000, 5000, 10000, 20000, 50000]

  return (
    candidateStepsM.find((stepM) => selectedDistanceM / stepM <= 14) ??
    candidateStepsM[candidateStepsM.length - 1]
  )
}

const buildTickFC = (): GeoJSON.FeatureCollection<GeoJSON.Point, TickFeatureProperties> => {
  const range = effectiveRange.value
  const selectedDistanceM = range.end - range.start
  const stepM = getTickStepM(selectedDistanceM)
  if (selectedDistanceM < stepM * 1.4) {
    return { type: 'FeatureCollection', features: [] }
  }

  const features: Array<GeoJSON.Feature<GeoJSON.Point, TickFeatureProperties>> = []
  for (
    let distanceFromStartM = stepM;
    distanceFromStartM < selectedDistanceM;
    distanceFromStartM += stepM
  ) {
    const point = getPointAtDistance(range.start + distanceFromStartM)
    if (!point) continue

    const label = formatTickDistance(distanceFromStartM)
    features.push({
      type: 'Feature',
      properties: { label, radius: Math.max(12, Math.min(18, 10 + label.length * 2)) },
      geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
    })
  }

  return { type: 'FeatureCollection', features }
}

const getForegroundLayerId = (map: MapLibreMap) => {
  const layers = map.getStyle().layers ?? []
  for (const layer of layers) {
    const id = layer.id
    if (
      id === 'photo-clusters' ||
      id === 'photo-unclustered-point' ||
      id.startsWith('clusters-') ||
      id.startsWith('unclustered-point-') ||
      id.startsWith('cluster-count-')
    ) {
      return id
    }
  }

  return undefined
}

const refreshSources = () => {
  const map = mapInstance.value
  if (!map || !layersInitialized) return
  ;(map.getSource(LINE_SOURCE_ID) as GeoJSONSource | undefined)?.setData(buildLineFC())
  ;(map.getSource(HANDLE_SOURCE_ID) as GeoJSONSource | undefined)?.setData(buildHandleFC())
  ;(map.getSource(TICK_SOURCE_ID) as GeoJSONSource | undefined)?.setData(buildTickFC())
}

const distanceToSegment = (
  location: maplibregl.LngLat,
  start: TrackProfilePoint,
  end: TrackProfilePoint,
): { distanceM: number; distanceFromTrackM: number } | null => {
  const originLatRad = start.lat * DEG_TO_RAD
  const toLocalMeters = (lat: number, lon: number) => ({
    x: (lon - start.lon) * DEG_TO_RAD * Math.cos(originLatRad) * EARTH_RADIUS_M,
    y: (lat - start.lat) * DEG_TO_RAD * EARTH_RADIUS_M,
  })

  const projectedEnd = toLocalMeters(end.lat, end.lon)
  const projectedLocation = toLocalMeters(location.lat, location.lng)
  const segmentLengthSq = projectedEnd.x ** 2 + projectedEnd.y ** 2
  if (segmentLengthSq <= 0) return null

  const t = Math.max(
    0,
    Math.min(
      1,
      (projectedLocation.x * projectedEnd.x + projectedLocation.y * projectedEnd.y) /
        segmentLengthSq,
    ),
  )
  const nearestX = projectedEnd.x * t
  const nearestY = projectedEnd.y * t

  return {
    distanceM: start.distanceM + (end.distanceM - start.distanceM) * t,
    distanceFromTrackM: Math.hypot(projectedLocation.x - nearestX, projectedLocation.y - nearestY),
  }
}

const findNearestDistanceOnTrack = (location: maplibregl.LngLat): number | null => {
  if (props.analysis.points.length < 2) return null

  let bestMatch: { distanceM: number; distanceFromTrackM: number } | null = null
  for (let index = 1; index < props.analysis.points.length; index += 1) {
    const match = distanceToSegment(
      location,
      props.analysis.points[index - 1],
      props.analysis.points[index],
    )
    if (!match) continue
    if (!bestMatch || match.distanceFromTrackM < bestMatch.distanceFromTrackM) {
      bestMatch = match
    }
  }

  return bestMatch?.distanceM ?? null
}

const updateDraggedHandle = (lngLat: maplibregl.LngLat) => {
  if (!activeHandle) return

  const distanceM = findNearestDistanceOnTrack(lngLat)
  if (distanceM === null) return

  const currentRange = effectiveRange.value
  const nextRange =
    activeHandle === 'start'
      ? normalizeRange({ start: distanceM, end: currentRange.end }, 'start')
      : normalizeRange({ start: currentRange.start, end: distanceM }, 'end')

  emit('update:range', nextRange)
}

const handleDragStart = (event: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent) => {
  const feature = event.features?.[0]
  const handle = feature?.properties?.handle as 'start' | 'end' | undefined
  if (!handle) return

  event.preventDefault()
  activeHandle = handle
  const map = mapInstance.value
  map?.dragPan.disable()
  if (map) map.getCanvas().style.cursor = 'grabbing'
}

const handleDragMove = (event: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
  if (!activeHandle) return
  event.preventDefault()
  updateDraggedHandle(event.lngLat)
}

const handleDragEnd = () => {
  if (!activeHandle) return
  activeHandle = null
  const map = mapInstance.value
  map?.dragPan.enable()
  if (map) map.getCanvas().style.cursor = ''
}

const handleZoom = () => {
  const map = mapInstance.value
  if (!map) return
  mapZoom.value = map.getZoom()
  refreshSources()
}

const handleHandleMouseEnter = () => {
  const map = mapInstance.value
  if (!map) return
  map.getCanvas().style.cursor = 'grab'
}

const handleHandleMouseLeave = () => {
  const map = mapInstance.value
  if (!map || activeHandle) return
  map.getCanvas().style.cursor = ''
}

const bindInteractions = (map: MapLibreMap) => {
  map.on('mousedown', HANDLE_LAYER_ID, handleDragStart)
  map.on('touchstart', HANDLE_LAYER_ID, handleDragStart)
  map.on('mousemove', handleDragMove)
  map.on('touchmove', handleDragMove)
  map.on('mouseup', handleDragEnd)
  map.on('touchend', handleDragEnd)
  map.on('touchcancel', handleDragEnd)
  map.on('zoomend', handleZoom)
  map.on('moveend', handleZoom)
  map.on('mouseenter', HANDLE_LAYER_ID, handleHandleMouseEnter)
  map.on('mouseleave', HANDLE_LAYER_ID, handleHandleMouseLeave)
}

const unbindInteractions = (map: MapLibreMap) => {
  map.off('mousedown', HANDLE_LAYER_ID, handleDragStart)
  map.off('touchstart', HANDLE_LAYER_ID, handleDragStart)
  map.off('mousemove', handleDragMove)
  map.off('touchmove', handleDragMove)
  map.off('mouseup', handleDragEnd)
  map.off('touchend', handleDragEnd)
  map.off('touchcancel', handleDragEnd)
  map.off('zoomend', handleZoom)
  map.off('moveend', handleZoom)
  map.off('mouseenter', HANDLE_LAYER_ID, handleHandleMouseEnter)
  map.off('mouseleave', HANDLE_LAYER_ID, handleHandleMouseLeave)
}

const initializeLayers = () => {
  const map = mapInstance.value
  if (!map || layersInitialized || props.analysis.points.length < 2) return

  const beforeLayerId = getForegroundLayerId(map)
  // The selected segment is an active interaction line, so keep it with its ticks and handles in
  // the foreground above basemap labels and POIs, but below app-owned marker layers when present.
  mapZoom.value = map.getZoom()

  map.addSource(LINE_SOURCE_ID, {
    type: 'geojson',
    data: buildLineFC(),
  })
  map.addLayer(
    {
      id: LINE_HALO_LAYER_ID,
      type: 'line',
      source: LINE_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#ffffff',
        'line-width': 12,
        'line-opacity': 0.9,
      },
    },
    beforeLayerId,
  )
  map.addLayer(
    {
      id: LINE_LAYER_ID,
      type: 'line',
      source: LINE_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': selectionColor.value,
        'line-width': 6,
      },
    },
    beforeLayerId,
  )

  map.addSource(TICK_SOURCE_ID, {
    type: 'geojson',
    data: buildTickFC(),
  })
  map.addLayer(
    {
      id: TICK_CIRCLE_LAYER_ID,
      type: 'circle',
      source: TICK_SOURCE_ID,
      paint: {
        // Solid track-colour fill + a white ring + a dark text halo (below) keeps the distance
        // markers legible on any basemap, light or dark.
        'circle-radius': ['get', 'radius'],
        'circle-color': selectionColor.value,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 3,
        'circle-opacity': 1,
      },
    },
    beforeLayerId,
  )
  map.addLayer(
    {
      id: TICK_LABEL_LAYER_ID,
      type: 'symbol',
      source: TICK_SOURCE_ID,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 12,
        'text-font': ['Open Sans Bold'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        // White numerals with a dark halo: legible on a track colour of any brightness.
        'text-color': '#ffffff',
        'text-halo-color': '#1f2937',
        'text-halo-width': 1.2,
      },
    },
    beforeLayerId,
  )

  map.addSource(HANDLE_SOURCE_ID, {
    type: 'geojson',
    data: buildHandleFC(),
  })
  map.addLayer(
    {
      id: HANDLE_HALO_LAYER_ID,
      type: 'circle',
      source: HANDLE_SOURCE_ID,
      paint: {
        'circle-radius': 16,
        'circle-color': '#ffffff',
        'circle-opacity': 0.95,
        'circle-stroke-color': '#111827',
        'circle-stroke-width': 1,
      },
    },
    beforeLayerId,
  )
  map.addLayer(
    {
      id: HANDLE_LAYER_ID,
      type: 'circle',
      source: HANDLE_SOURCE_ID,
      paint: {
        'circle-radius': 13,
        'circle-color': '#374151',
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2.5,
      },
    },
    beforeLayerId,
  )
  map.addLayer(
    {
      id: HANDLE_LABEL_LAYER_ID,
      type: 'symbol',
      source: HANDLE_SOURCE_ID,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 15,
        'text-font': ['Open Sans Bold'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
      },
    },
    beforeLayerId,
  )

  bindInteractions(map)
  boundMap = map
  layersInitialized = true
}

const removeLayers = () => {
  const map = boundMap ?? mapInstance.value
  if (!map) return

  unbindInteractions(map)
  handleDragEnd()

  for (const layerId of [
    HANDLE_LABEL_LAYER_ID,
    HANDLE_LAYER_ID,
    HANDLE_HALO_LAYER_ID,
    TICK_LABEL_LAYER_ID,
    TICK_CIRCLE_LAYER_ID,
    LINE_LAYER_ID,
    LINE_HALO_LAYER_ID,
  ]) {
    try {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Failed to remove track segment selection layer', {
        layerId,
        error,
      })
    }
  }

  for (const sourceId of [HANDLE_SOURCE_ID, TICK_SOURCE_ID, LINE_SOURCE_ID]) {
    try {
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    } catch (error) {
      logService.warn(LOG_CATEGORIES.MAP, 'Failed to remove track segment selection source', {
        sourceId,
        error,
      })
    }
  }

  boundMap = null
  layersInitialized = false
}

const applyAccentColors = () => {
  const map = boundMap ?? mapInstance.value
  if (!map || !layersInitialized) return
  try {
    if (map.getLayer(LINE_LAYER_ID)) {
      map.setPaintProperty(LINE_LAYER_ID, 'line-color', selectionColor.value)
    }
    if (map.getLayer(TICK_CIRCLE_LAYER_ID)) {
      map.setPaintProperty(TICK_CIRCLE_LAYER_ID, 'circle-color', selectionColor.value)
    }
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to apply segment-selection accent colours', {
      error,
    })
  }
}

watch(
  [mapInstance, mapLoaded],
  ([map, loaded], previousValues) => {
    const previousMap = previousValues?.[0] ?? null
    if (previousMap && previousMap !== map) {
      removeLayers()
    }
    if (map && loaded) {
      initializeLayers()
      refreshSources()
    }
  },
  { immediate: true },
)

watch(
  () => props.analysis,
  () => {
    if (props.analysis.points.length < 2) {
      removeLayers()
      return
    }
    initializeLayers()
    refreshSources()
  },
  { deep: true },
)

watch(
  () => props.range,
  () => refreshSources(),
  { deep: true },
)

watch(
  () => props.accentColor,
  () => applyAccentColors(),
)

onUnmounted(() => {
  removeLayers()
})
</script>

<template>
  <div class="track-segment-selection-layer"></div>
</template>
